import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Grid,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';

interface FilingPeriodFilterProps {
  filingPeriod: string;
  onFilingPeriodChange: (period: string) => void;
  lookbackPeriod?: number;
  onLookbackPeriodChange?: (period: number) => void;
  availablePeriods?: Array<{
    value: string;
    label: string;
    transaction_count?: number;
    total_amount?: number;
  }>;
  onRefresh?: () => void;
  loading?: boolean;
  showLookbackPeriod?: boolean;
  showRefreshButton?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
}

const FilingPeriodFilter: React.FC<FilingPeriodFilterProps> = ({
  filingPeriod,
  onFilingPeriodChange,
  lookbackPeriod = 1,
  onLookbackPeriodChange,
  availablePeriods = [],
  onRefresh,
  loading = false,
  showLookbackPeriod = true,
  showRefreshButton = true,
  size = 'small',
  fullWidth = false,
}) => {
  const theme = useTheme();
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);

  // Generate current period option
  const currentPeriod = `${currentYear}-${currentMonth.toString().padStart(2, '0')}`;
  const currentPeriodLabel = `${new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;

  // Combine current period with available periods
  const allPeriods = [
    {
      value: '',
      label: 'Current Period',
      transaction_count: 0,
      total_amount: 0,
    },
    {
      value: currentPeriod,
      label: currentPeriodLabel,
      transaction_count: 0,
      total_amount: 0,
    },
    ...availablePeriods,
  ];

  // Remove duplicates based on value
  const uniquePeriods = allPeriods.filter((period, index, self) => 
    index === self.findIndex(p => p.value === period.value)
  );

  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      onRefresh();
    }
  }, [onRefresh]);

  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
      {/* Filing Period Selector */}
      <FormControl size={size} sx={{ minWidth: fullWidth ? '100%' : 200 }}>
        <InputLabel id="filing-period-label">Filing Period</InputLabel>
        <Select
          labelId="filing-period-label"
          value={filingPeriod}
          label="Filing Period"
          onChange={(e) => onFilingPeriodChange(e.target.value)}
        >
          {uniquePeriods.map((period) => (
            <MenuItem key={period.value} value={period.value}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Box>
                  <Typography variant="body2">
                    {period.label}
                  </Typography>
                  {period.transaction_count !== undefined && period.transaction_count > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {period.transaction_count} transactions
                    </Typography>
                  )}
                </Box>
                {period.total_amount !== undefined && period.total_amount > 0 && (
                  <Chip
                    label={`$${period.total_amount.toLocaleString()}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Lookback Period Selector */}
      {showLookbackPeriod && onLookbackPeriodChange && (
        <FormControl size={size} sx={{ minWidth: fullWidth ? '100%' : 150 }}>
          <InputLabel id="lookback-period-label">Lookback</InputLabel>
          <Select
            labelId="lookback-period-label"
            value={lookbackPeriod}
            label="Lookback"
            onChange={(e) => onLookbackPeriodChange(Number(e.target.value))}
          >
            <MenuItem value={1}>1 Month</MenuItem>
            <MenuItem value={3}>3 Months</MenuItem>
            <MenuItem value={6}>6 Months</MenuItem>
            <MenuItem value={12}>12 Months</MenuItem>
          </Select>
        </FormControl>
      )}

      {/* Refresh Button */}
      {showRefreshButton && onRefresh && (
        <Button
          variant="outlined"
          onClick={handleRefresh}
          disabled={loading}
          startIcon={<RefreshIcon />}
          size={size}
          sx={{ 
            minWidth: fullWidth ? '100%' : 'auto',
            height: size === 'small' ? '40px' : '56px'
          }}
        >
          Refresh
        </Button>
      )}

      {/* Period Info */}
      {filingPeriod && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          <Typography variant="caption" color="text.secondary">
            {filingPeriod === '' ? 'Current Period' : 
             availablePeriods.find(p => p.value === filingPeriod)?.label || 
             new Date(filingPeriod + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default FilingPeriodFilter;
